import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, MailCheck } from 'lucide-react';
import { motion } from 'motion/react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { authForgotPassword } from '@/shared/api/generated/auth/auth';
import { ApiError, NetworkError } from '@/shared/api/http';
import { springs } from '@/shared/lib/motion';
import { t } from '@/shared/i18n';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { Input } from '@/shared/ui/Input';

import styles from '@/pages/LoginPage/LoginPage.module.scss';

const schema = z.object({ email: z.email(t.login.invalidEmail) });

type FormValues = z.infer<typeof schema>;

/**
 * «Забыли пароль?» — адрес, и ответ всегда один: «проверьте почту». Есть
 * такой адрес или нет, форма не говорит — иначе она становится способом
 * проверить, кто у нас зарегистрирован.
 */
export function ForgotPage() {
  const [sentTo, setSentTo] = useState<string | null>(null);
  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: { email: '' } });

  const submit = form.handleSubmit(async (values) => {
    try {
      await authForgotPassword({ email: values.email });
      setSentTo(values.email);
    } catch (cause) {
      const message =
        cause instanceof ApiError
          ? cause.detail
          : cause instanceof NetworkError
            ? t.common.networkError
            : t.common.unknownError;

      form.setError('email', { message });
    }
  });

  if (sentTo !== null) {
    return (
      <motion.div
        className={styles['card']}
        initial={{ opacity: 0, y: 12, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={springs.spring}
      >
        <header className={styles['head']}>
          <h1 className={styles['title']}>
            <MailCheck /> {t.forgot.sentTitle}
          </h1>
          <p className={styles['subtitle']}>{t.forgot.sentText(sentTo)}</p>
        </header>
        <Button asChild variant="outline" tone="neutral" iconLeft={<ArrowLeft />}>
          <Link to="/login">{t.forgot.back}</Link>
        </Button>
      </motion.div>
    );
  }

  return (
    <motion.form
      className={styles['card']}
      onSubmit={(event) => {
        void submit(event);
      }}
      noValidate
      initial={{ opacity: 0, y: 12, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={springs.spring}
    >
      <header className={styles['head']}>
        <h1 className={styles['title']}>{t.forgot.title}</h1>
        <p className={styles['subtitle']}>{t.forgot.subtitle}</p>
      </header>

      <Field label={t.login.email} required error={form.formState.errors.email?.message}>
        <Input
          type="email"
          autoComplete="username"
          placeholder="you@flow-do.ru"
          autoFocus
          {...form.register('email')}
        />
      </Field>

      <Button
        type="submit"
        size="lg"
        fullWidth
        loading={form.formState.isSubmitting}
        iconRight={<ArrowRight />}
      >
        {t.forgot.submit}
      </Button>

      <p className={styles['switch']}>
        <Link to="/login" className={styles['switch__link']}>
          {t.forgot.back}
        </Link>
      </p>
    </motion.form>
  );
}
